import { useEffect, useState } from "react";
import useXRErrors from "./useXRErrors";
import useUserDevice from "./useUserDevice";
import { AppPermissionStatus } from "@webcore/core/permissions/permissions.status";

export interface XRErrorConfig {
  defaultMessage: string;
  iOSMessage: string;
  androidMessage?: string;
}

/**
 * Does the current Android browser actually implement WebXR immersive-ar (when
 * the device supports it)? Chrome, Edge and Samsung Internet do; Opera, Firefox
 * and in-app webviews do not.
 *
 * Used ONLY to pick the right error message after a launch has already failed —
 * never to gate the launch (capability/isSessionSupported does that). The WebXR
 * API can't tell "capable device, incapable browser" (Opera) apart from
 * "incapable device" (old phone in Chrome) — both report navigator.xr present +
 * isSessionSupported false. The browser we're already in is the only signal that
 * separates them: if AR fails in an AR-capable browser, the device is the problem
 * and "open in Chrome" would be useless advice.
 */
function isArCapableAndroidBrowser(ua: string): boolean {
  // Browsers where switching to Chrome WOULD help → not AR-capable.
  if (/OPR\//.test(ua) || /\bOPT\//.test(ua)) return false; // Opera / Opera Touch
  if (/Firefox\//.test(ua) || /FxiOS/.test(ua)) return false; // Firefox
  if (/;\s*wv\)/.test(ua)) return false; // Android WebView (in-app browsers)
  // Chromium browsers that implement immersive-ar: Chrome, Edge, Samsung Internet.
  return /Chrome\//.test(ua) || /SamsungBrowser\//.test(ua);
}

/**
 * Provides user-friendly guidance for XR issues
 */
export function useXRErrorGuidance(customErrors?: {
  deniedCamera?: XRErrorConfig;
  deniedMotion?: XRErrorConfig;
  denied?: XRErrorConfig;
  notDefined?: XRErrorConfig;
  notSupported?: XRErrorConfig;
}) {
  const { xrPermissionStatus, xrOtherStatus, xrSupportStatus } = useXRErrors();
  const { device } = useUserDevice();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isAndroid = device === "Android";

  const androidGuidance = {
    camera: ` <ul class="pt-8 px-10 text-left flex flex-col gap-1 text-[18px] list-decimal">
                <li>
                  Tap the <span class="font-bold"> icon</span> next
                  to the website address
                </li>
                <li>
                  Select <span class="font-bold">Permissions</span>
                </li>
                <li>
                  Enable <span class="font-bold">Camera</span> and/or  <span class="font-bold">Microphone</span>
                </li>
              </ul>`,
    webxr: `<ul class="pt-8 px-10 text-left flex flex-col gap-1 text-[22px] list-decimal">
                <li>
                  Tap the <span class="font-bold">lock icon</span> next
                  to the website address
                </li>
                <li>
                  Select <span class="font-bold">Permissions</span>
                </li>
                <li>
                  Enable <span class="font-bold">Augmented Reality</span>
                </li>
              </ul>`,
  };

  const defaultErrors = {
    deniedCamera: {
      defaultMessage: "Allow camera access in the app settings.",
      iOSMessage:
        "Camera & Microphone <br />access is required.<br /><br />Quit and reopen the <br />browser to allow access.",
      androidMessage: androidGuidance.camera,
    },
    deniedMotion: {
      defaultMessage:
        "Motion & Orientation <br />access is required. <br /><br />Quit and reopen the <br />browser to allow access.",
    },
    denied: {
      defaultMessage: "Allow camera access in the app settings.",
      androidMessage: androidGuidance.webxr,
    },
    notDefined: {
      defaultMessage: "WebXR is not defined in this browser.",
    },
    notSupported: {
      defaultMessage: "WebXR is not supported on this device.",
      // iOS only reaches "not supported" via the sub-16.4 App Clip gate in
      // useXRCheckInline (iOS ≥ 16.4 → App Clip; native WebXR never exists on
      // iOS), so an iOS device here is always below the App Clip's min version.
      iOSMessage:
        "Your device does not support App Clips.<br /><br />Update to iOS 16.4 or later.",
    },
  };

  const errors = {
    deniedCamera: {
      ...defaultErrors.deniedCamera,
      ...customErrors?.deniedCamera,
    },
    deniedMotion: {
      ...defaultErrors.deniedMotion,
      ...customErrors?.deniedMotion,
    },
    denied: { ...defaultErrors.denied, ...customErrors?.denied },
    notDefined: { ...defaultErrors.notDefined, ...customErrors?.notDefined },
    notSupported: {
      ...defaultErrors.notSupported,
      ...customErrors?.notSupported,
    },
  };

  useEffect(() => {
    setErrorMessage(null);

    switch (xrPermissionStatus) {
      case AppPermissionStatus.DENIED_CAMERA: {
        const config = errors.deniedCamera;
        if (isAndroid) {
          setErrorMessage(config.defaultMessage + androidGuidance.camera);
        } else if (device === "iOS") {
          setErrorMessage(config.iOSMessage);
        } else {
          setErrorMessage(config.defaultMessage);
        }
        break;
      }

      case AppPermissionStatus.DENIED_MOTION: {
        const config = errors.deniedMotion;
        setErrorMessage(config.defaultMessage);
        break;
      }

      case AppPermissionStatus.DENIED: {
        const config = errors.denied;
        if (isAndroid) {
          setErrorMessage(config.defaultMessage + androidGuidance.webxr);
        } else {
          setErrorMessage(config.defaultMessage);
        }
        break;
      }

      case AppPermissionStatus.GRANTED:
      case AppPermissionStatus.PROMPT:
      default:
        break;
    }

    if (xrOtherStatus === false) {
      const config = errors.notDefined;
      setErrorMessage(config.defaultMessage);
    }

    if (xrSupportStatus === false) {
      const config = errors.notSupported;
      if (device === "iOS" && config.iOSMessage) {
        setErrorMessage(config.iOSMessage);
      } else {
        setErrorMessage(config.defaultMessage);
      }
    }
  }, [
    xrPermissionStatus,
    device,
    xrOtherStatus,
    xrSupportStatus,
    isAndroid,
    errors,
  ]);

  // Whether to show the "open in Chrome" copy-link guidance instead of the
  // generic error message. Android only — iOS hands off to the App Clip.
  //   • otherStatus === false  : no WebXR API at all (Firefox, in-app webviews) —
  //     unambiguously a browser problem → Chrome helps.
  //   • supportStatus === false: navigator.xr exists but immersive-ar won't start.
  //     Only suggest Chrome when we're NOT already in an AR-capable browser;
  //     otherwise it's the device (old phone, no ARCore) and switching won't help.
  const inArCapableBrowser =
    typeof navigator !== "undefined" &&
    isArCapableAndroidBrowser(navigator.userAgent);
  const showCopyLinkGuidance =
    isAndroid &&
    (xrOtherStatus === false ||
      (xrSupportStatus === false && !inArCapableBrowser));

  return {
    errorMessage,
    permissionStatus: xrPermissionStatus,
    supportStatus: xrSupportStatus,
    otherStatus: xrOtherStatus,
    showCopyLinkGuidance,
  };
}
